import type { RootState } from '~/store/redux-store'
import { createApi } from '@reduxjs/toolkit/query/react'
import { createExternalExchangeRTQuery } from '~/lib/api/createExternalExchangeRTQuery'
import type { ExchangeCurrencyMap, ExchangeCurrencyType } from '~/components/exchange/types/exchangeCurrency'
import type { CurrencyExchangeRates, ProgramModel } from '~/models'
import { v4 as uuidv4 } from 'uuid'
import {
  ETIKETKA_PARTNER_CURRENCY,
  RUBLES_EXCHANGE_CURRENCY_FROM,
  RUBLES_EXCHANGE_CURRENCY_TO,
} from '~/components/exchange/constants'
import { exchangeRoubles } from '~/store/api/mock/exchangeRoubles'
import { ProgramSelector } from '~/store/selector/programs'
import { productProgramsAllProgramsSelector } from '~/store/api/options'
import type { ExchangePartnerResponse } from '~/components/exchange/types/exchangePartnerResponse'
import type { ProcessorsRegisteredProgramType } from '~/components/exchange/types/processorsRegisteredPrograms'
import { balanceApi } from '~/store/api/balance'
import { fetchInternalCurrencyExchange } from './helpers/fetchInternalCurrencyExchange'

const externalExchangeRTQuery = createExternalExchangeRTQuery()

export const exchangeApi = createApi({
  reducerPath: 'exchangeQuery',
  keepUnusedDataFor: 300,
  baseQuery: externalExchangeRTQuery({
    baseUrl: '',
  }),
  endpoints: (build) => ({
    getAllExchangeData: build.query<ExchangeCurrencyMap, void>({
      async queryFn(_args, queryApi, _extraOptions, fetchWithBaseQuery) {
        const state = queryApi.getState() as RootState
        const programs = ProgramSelector(state)
        const activeProgramCodes: string[] = productProgramsAllProgramsSelector(state)

        const [resRatesForRoubles, exchangePartnersInfoResponse, exchangeRegisterStatusesResponse] = await Promise.all([
          fetchInternalCurrencyExchange({
            currencyFrom: RUBLES_EXCHANGE_CURRENCY_FROM,
            currencyTo: RUBLES_EXCHANGE_CURRENCY_TO,
          }),
          fetchWithBaseQuery({
            url: 'v1/partner',
            method: 'GET',
          }),
          fetchWithBaseQuery({
            url: `v1/customer/${state.exchange.converterBindingId}`,
            method: 'GET',
          }),
        ])

        if ('error' in resRatesForRoubles) return { error: 'Something in API went wrong' }

        const exchangePartnersInfoError = exchangePartnersInfoResponse.error as { status: number; data: unknown }
        if (exchangePartnersInfoError) return { error: 'Something in API went wrong' }

        const exchangeCurrenciesError = exchangeRegisterStatusesResponse.error as { status: number; data: unknown }
        if (exchangeCurrenciesError) return { error: 'Something in API went wrong' }

        const processorsRegisteredProgramsData = exchangeRegisterStatusesResponse!.data as unknown as {
          attributes: ProcessorsRegisteredProgramType[]
        }

        const converted = makeExchangeData({
          programs,
          state,
          activeProgramCodes,
          ratesForRoubles: resRatesForRoubles.data,
          processorsRegisteredPrograms: processorsRegisteredProgramsData.attributes,
          responseData: exchangePartnersInfoResponse.data as unknown as ExchangePartnerResponse[],
        })

        return {
          data: converted,
        }
      },
    }),

    processorRegistration: build.mutation<null, void>({
      async queryFn(_args, queryApi, _extraOptions, fetchWithBaseQuery) {
        const state = queryApi.getState() as RootState

        const { sourceProgram, destinationProgram, converterBindingId } = getExchangeReduxData(state)

        if (!sourceProgram || !destinationProgram || !converterBindingId) {
          return { error: 'Has no programs for registration on Processor' }
        }

        const sourceProgramParams = makeRegistrationParams(sourceProgram)
        const destinationProgramParams = makeRegistrationParams(destinationProgram)

        const sourceHasRegistered = sourceProgramParams === 'hasRegistered'
        const destinationHasRegistered = destinationProgramParams === 'hasRegistered'

        if (sourceHasRegistered && destinationHasRegistered) return { data: null }

        const registrationParams = []
        if (!sourceHasRegistered) registrationParams.push(...sourceProgramParams)
        if (!destinationHasRegistered) registrationParams.push(...destinationProgramParams)

        const registrationResponse = await fetchWithBaseQuery({
          url: `v1/customer/${converterBindingId}/attribute`,
          method: 'POST',
          data: registrationParams,
        })

        const registrationResponseError = registrationResponse.error as { status: number; data: unknown }
        if (registrationResponseError) return { error: 'Something in API went wrong' }

        if (!sourceHasRegistered) {
          const authorizationSrcResponse = await fetchWithBaseQuery({
            url: `v1/customer/${converterBindingId}/authorization`,
            method: 'POST',
            data: {
              handle: {
                partner_uid: sourceProgram.partner.uid,
                currency_code: sourceProgram.currency,
                key: sourceProgram.currency,
              },
              attributes: sourceProgram.partner.processorRegistrationParams,
            },
          })

          const authorizationSrcResponseError = authorizationSrcResponse.error as { status: number; data: unknown }
          if (authorizationSrcResponseError) return { error: 'Something in API went wrong' }
        }

        if (!destinationHasRegistered) {
          const authorizationDestResponse = await fetchWithBaseQuery({
            url: `v1/customer/${converterBindingId}/authorization`,
            method: 'POST',
            data: {
              handle: {
                partner_uid: destinationProgram.partner.uid,
                currency_code: destinationProgram.currency,
                key: destinationProgram.currency,
              },
              attributes: destinationProgram.partner.processorRegistrationParams,
            },
          })

          const authorizationDestResponseError = authorizationDestResponse.error as { status: number; data: unknown }
          if (authorizationDestResponseError) return { error: 'Something in API went wrong' }
        }

        return { data: null }
      },
    }),
    exchangePoints: build.mutation<null, void>({
      async queryFn(_args, queryApi, _extraOptions, fetchWithBaseQuery) {
        const request_uuid = uuidv4()
        const state = queryApi.getState() as RootState

        const { sourceProgram, destinationProgram, converterBindingId, pointsToCharge } = getExchangeReduxData(state)
        if (!sourceProgram || !destinationProgram || !converterBindingId || !pointsToCharge) {
          return { error: 'Exchange impossible. API error' }
        }

        const res = await fetchWithBaseQuery({
          url: `v1/operation/transfer`,
          method: 'POST',
          data: {
            request_uid: request_uuid,
            code: 'TRANSFER',
            parties: {
              source: {
                loyalty_code: converterBindingId,
                amount: pointsToCharge,
                authorization: {
                  partner_uid: sourceProgram.partner.uid,
                  currency_code: sourceProgram.currency,
                  key: sourceProgram.currency,
                },
              },
              target: {
                loyalty_code: converterBindingId,
                authorization: {
                  partner_uid: destinationProgram.partner.uid,
                  currency_code: destinationProgram.currency,
                  key: destinationProgram.currency,
                },
              },
            },
            timeouts: {
              realtime: 'PT5S',
              overdue: 'P1D',
            },
          },
        })

        queryApi.dispatch(balanceApi.util.invalidateTags(['Total']))

        const resError = res.error as { status: number; data: unknown }
        if (resError) return { error: 'Exchange impossible. API error' }

        return { data: null }
      },
    }),
  }),
})

export const { useGetAllExchangeDataQuery, useProcessorRegistrationMutation, useExchangePointsMutation } = exchangeApi

function getExchangeReduxData(state: RootState) {
  const exchangeData = exchangeApi.endpoints.getAllExchangeData.select()(state)
  const exchangeSlice = state.exchange

  if (!exchangeData.data)
    return {
      userId: null,
      sourceProgram: null,
      pointsToCharge: null,
      destinationProgram: null,
      converterBindingId: null,
    }

  const partnerPrograms = exchangeData.data
  const { sourceProgramId, destinationProgramId, converterBindingId, pointsToCharge, userId } = exchangeSlice

  const sourceProgram = sourceProgramId ? partnerPrograms[sourceProgramId] : null
  const destinationProgram = destinationProgramId ? partnerPrograms[destinationProgramId] : null

  return {
    userId,
    sourceProgram,
    pointsToCharge,
    destinationProgram,
    converterBindingId,
  }
}

function makeRegistrationParams({ partner }: ExchangeCurrencyType) {
  if (partner.processorRegistrationId) return 'hasRegistered'

  return partner.processorRegistrationParams.map(({ code, value }) => ({
    code,
    key: code,
    value,
    source: 'client',
    is_verified: true,
  }))
}

interface MakeExchangeDataProps {
  programs: ProgramModel[]
  state: RootState
  activeProgramCodes: string[]
  ratesForRoubles: CurrencyExchangeRates
  responseData: ExchangePartnerResponse[]
  processorsRegisteredPrograms: ProcessorsRegisteredProgramType[]
}

function makeExchangeData({
  activeProgramCodes,
  programs,
  state,
  ratesForRoubles,
  responseData,
  processorsRegisteredPrograms,
}: MakeExchangeDataProps): ExchangeCurrencyMap {
  const etiketkaUserId = state.exchange.userId
  const etiketkaProfileMnemocode = state.authentication.profile.profile_mnemocode

  const activePrograms = programs.filter(({ entryClass }) => activeProgramCodes.includes(entryClass))
  const activeCurrenciesCodes = new Set(activePrograms.map(({ entryClass }) => entryClass))
  const partnerAccountIdsByCurrency = Object.fromEntries(
    activePrograms.map(({ entryClass, entryName }) => [entryClass, entryName ?? null])
  )
  const filteredResponseData = responseData
    .map(({ partner, currencies }) => ({
      partner,
      currencies: currencies.filter(({ code }) => activeCurrenciesCodes.has(code)),
    }))
    .filter(({ currencies }) => currencies.length > 0)

  const roublesTerms = {
    rate: ratesForRoubles.details[0].comission,
    min: ratesForRoubles.minOperationAllowed,
    max: ratesForRoubles.maxOperationAllowed,
  }
  const exchangeCurrencies: ExchangeCurrencyMap = {
    RUB: exchangeRoubles,
  }

  for (const { partner, currencies } of filteredResponseData) {
    for (const { code, exchange_rates, limits, authorization } of currencies) {
      const withdrawalLimit = limits.withdrawal.find(({ type }) => type === 'PER_TRANSACTION')
      const targetExchangeTermsFromWithdrawal: NonNullable<ExchangeCurrencyType['targetExchangeTerms']> = {}

      for (const { currency_code, value } of exchange_rates.withdrawal) {
        // eslint-disable-next-line no-continue
        if (!activeCurrenciesCodes.has(currency_code)) continue

        targetExchangeTermsFromWithdrawal[currency_code] = {
          rate: value,
          min: withdrawalLimit?.min ?? 1,
          ...(typeof withdrawalLimit?.max === 'number' ? { max: withdrawalLimit.max } : {}),
        }
      }

      const currentProgram = activePrograms.find(({ entryClass }) => entryClass === code)
      const partnerName = partner.name
      const currentProcessor = processorsRegisteredPrograms.find(({ source }) => source.code === partnerName)

      const authorizationCode = authorization.attributes[0].code
      const processorValue =
        currentProcessor?.value && currentProcessor?.key === authorizationCode ? currentProcessor.value : null

      const processorRegistrationParams = authorization.attributes.map(({ code: authorizationCode1 }) => {
        let value: string | null = null

        switch (true) {
          case code === ETIKETKA_PARTNER_CURRENCY && authorizationCode1 === 'profile_mnemocode':
            value = etiketkaProfileMnemocode!
            break
          case code === ETIKETKA_PARTNER_CURRENCY && authorizationCode1 === 'user_id':
            value = etiketkaUserId!
            break
          default:
            value = partnerAccountIdsByCurrency[code]
        }

        return {
          code: authorizationCode1,
          value,
        }
      })

      exchangeCurrencies[code] = {
        currency: code,
        appearance: {
          mnemocode: currentProgram?.programMnemocode ?? '',
          name: currentProgram?.details.name ?? '',
          pluralNames: currentProgram?.details.currencyNames ?? '',
          currencyDigital: currentProgram?.details.currency ?? '',
          hasNotValidStatus: currentProgram?.entryStatus === 'E',
        },
        partner: {
          uid: partner.uid,
          name: partnerName,
          processorRegistrationId: processorValue,
          processorRegistrationParams,
        },
        targetExchangeTerms:
          exchange_rates.withdrawal.length > 0
            ? {
                ...targetExchangeTermsFromWithdrawal,
                RUB: roublesTerms,
              }
            : null,
      }
    }
  }

  return exchangeCurrencies
}
